import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTitle from '../../../components/page-title/PageTitle';
import IPage from '../../../interfaces/IPage';
import { Container, Form } from './DocumentTypeForm.styled';

import { MdOutlineAssignmentReturn } from 'react-icons/md';
import { useTypeDocumentService } from '../../../hooks/services/useTypeDocumentService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import IFormError from '../../../interfaces/IFormError';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider, ptBR, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import IValidationOption from '../../../interfaces/IValidationOption';
import { useTenantService } from '../../../hooks/services/useTenantService';
import SaveIcon from '@mui/icons-material/Save';
import { SelectableCard } from './SelectableCard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import IHcmRoutine from '../../../interfaces/IHcmRoutine';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface DocumentTypeFormProps extends IPage { }

const initialDocumentType: ITypeDocument = {
  description: '',
  isActive: true,
  order: 0,
  cidRequired: false,
  crmRequired: false,
  allowComments: false,
  hospitalRequired: false,
  documentsAllowYear: 0,
  availableToEmployee: true,
  quantityTimeInDaysDefault: 0,
  quantityTimeInHoursDefault: 0,
  showCID: false,
  showCRM: false,
  showHospital: false,
  validateLatenessFromBackoffice: false,
  hoursOfLateness: 0,
  validationOptionId: null,
  launchInHours: false,
  launchInDay: false,
  hcmRoutine: 'LEAVE',
};

const hcmRoutineLabels: Record<keyof IHcmRoutine, string> = {
  LEAVE: 'Afastamento',
  ANNOTATION: 'Anotação',
  EXTERNAL_CERTIFICATE: 'Atestado externo',
  NONE: 'Nenhum',
};

function decimalParaHora(decimal: number) {
  const horas = Math.floor(decimal);
  const minutos = Math.round((decimal - horas) * 60);
  return moment({ hour: horas, minute: minutos });
}

const DocumentTypeForm = (props: DocumentTypeFormProps) => {
  window.document.title = props.title;

  const [startTime, setStartTime] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'edit' | 'new'>('edit');
  const [id, setId] = useState(0);
  const [typeDocument, setTypeDocument] = useState<ITypeDocument>({ ...initialDocumentType });
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [actionLabel, setActionLabel] = useState('INCLUSÃO');

  const [validationOptions, setValidationOptions] = useState<IValidationOption[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const request = useTypeDocumentService();
  const validationOptionRequest = useTenantService();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;

  const isEdit = hasAccessToAction(
    ["tipo-documento-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const schema = yup.object({
    description: yup.string().required('Campo obrigatório'),
    order: yup.number().min(1, 'Campo obrigatório'),
  });

  useEffect(() => {
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';
    const id = parseInt(location.pathname.split('/')[location.pathname.split('/').length - 2]);
    listValidationOptions();
    setAction(action);
    if (action === 'edit') {
      setActionLabel('EDIÇÃO');
    } else {
      setActionLabel('INCLUSÃO');
    }
    setId(id);
    if (id !== 0) {
      listTypeDocumentById(id);
      setStartTime(decimalParaHora(typeDocument?.quantityTimeInHoursDefault || 0));
    }
  }, []);

  const listTypeDocumentById = async (id: number): Promise<void> => {
    setLoading(true);
    await request
      .listById(id)
      .then(result => {
        setLoading(false);
        setTypeDocument({ ...result });
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const createTypeDocument = async (typeDocument: ITypeDocument): Promise<void> => {
    setLoading(true);
    const updatedTypeDocument = {
      ...typeDocument,
      quantityTime: typeDocument.quantityTimeInHoursDefault == null ? 0 : typeDocument.quantityTimeInHoursDefault,
      validationOptionId: typeDocument.validationOptionId === 0 ? null : typeDocument.validationOptionId,
    };
    await request
      .create(updatedTypeDocument)
      .then(result => {
        setLoading(false);
        setTypeDocument({ ...result });
        toastr.success('Documento cadastrado com sucesso!');
        navigate(`/tipo-documento`);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const updateTypeDocument = async (id: number, typeDocument: ITypeDocument): Promise<void> => {
    setLoading(true);
    await request
      .update(id, typeDocument)
      .then(result => {
        setLoading(false);
        setTypeDocument({ ...result });
        toastr.success('Documento atualizado com sucesso!');
        navigate(`/tipo-documento`);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const listValidationOptions = async (): Promise<void> => {
    setLoading(true);
    await validationOptionRequest
      .listValidationOptions()
      .then(result => {
        setValidationOptions(result);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const incrementDay = () => {
    setTypeDocument({
      ...typeDocument,
      order: typeDocument.order && parseInt(`${typeDocument.order}`) ? parseInt(`${typeDocument.order}`) + 1 : 1,
    });
  };

  const decrementDay = () => {
    setTypeDocument({
      ...typeDocument,
      order: typeDocument.order && parseInt(`${typeDocument.order}`) > 0 ? parseInt(`${typeDocument.order}`) - 1 : 0,
    });
  };

  const incrementAllowDocument = () => {
    setTypeDocument({
      ...typeDocument,
      documentsAllowYear:
        typeDocument.documentsAllowYear && parseInt(`${typeDocument.documentsAllowYear}`)
          ? parseInt(`${typeDocument.documentsAllowYear}`) + 1
          : 1,
    });
  };

  const decrementAllowDocument = () => {
    setTypeDocument({
      ...typeDocument,
      documentsAllowYear:
        typeDocument.documentsAllowYear && parseInt(`${typeDocument.documentsAllowYear}`) > 0
          ? parseInt(`${typeDocument.documentsAllowYear}`) - 1
          : 0,
    });
  };
  const incrementQuantityTimeInDaysDefault = () => {
    setTypeDocument({
      ...typeDocument,
      quantityTimeInDaysDefault:
        typeDocument.quantityTimeInDaysDefault && parseInt(`${typeDocument.quantityTimeInDaysDefault}`)
          ? parseInt(`${typeDocument.quantityTimeInDaysDefault}`) + 1
          : 1,
    });
  };

  const decrementQuantityTimeInDaysDefault = () => {
    setTypeDocument({
      ...typeDocument,
      quantityTimeInDaysDefault:
        typeDocument.quantityTimeInDaysDefault && parseInt(`${typeDocument.quantityTimeInDaysDefault}`) > 0
          ? parseInt(`${typeDocument.quantityTimeInDaysDefault}`) - 1
          : 0,
    });
  };

  function horaDecimal(value: number) {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);

    const time = moment()
      .startOf('day')
      .add(hours, 'hours')
      .add(minutes, 'minutes');

    setStartTime(time);
  }

  const handleCancel = async () => {
    if (action === 'edit') {
      listTypeDocumentById(id);
    } else {
      setTypeDocument({ ...initialDocumentType });
    }
  };

  const handleSaveClick = async () => {
    const typeDocumentToSave = {
      ...typeDocument,
      quantityTimeInHoursDefault:
        typeDocument.quantityTimeInHoursDefault == null ? 0 : typeDocument.quantityTimeInHoursDefault,
    };

    const result = await validation.validate(schema, typeDocumentToSave);
    setErrors(result.errors);

    if (!result.isValid) {
      return;
    }

    try {
      if (action === 'edit') {
        await updateTypeDocument(id, typeDocumentToSave);
      } else {
        await createTypeDocument(typeDocumentToSave);
      }
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
    }
  };
  useEffect(() => {
    if (typeDocument.quantityTimeInHoursDefault) {
      horaDecimal(typeDocument.quantityTimeInHoursDefault);
    }
  }, [typeDocument]);

  const FIELD_CONFIG = [
    { label: 'CRM', showKey: 'showCRM', reqKey: 'crmRequired' },
    { label: 'Local', showKey: 'showHospital', reqKey: 'hospitalRequired' },
    { label: 'CID', showKey: 'showCID', reqKey: 'cidRequired' },
  ] as const;

  function getStatusBadge(visible: boolean, required: boolean) {
    if (!visible) return { label: 'Oculto', color: 'default' as const };
    if (required) return { label: 'Visível + obrigatório', color: 'info' as const };
    return { label: 'Visível + opcional', color: 'success' as const };
  }

  return (
    <Container>
      <PageTitle title={`${actionLabel} DE TIPO DE DOCUMENTO`}>
        <Tooltip title="Voltar para listagem" placement="left-start">
          <IconButton onClick={() => navigate(`/tipo-documento`)} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição"
              placeholder="Informe a descrição do documento"
              value={typeDocument?.description}
              onChange={e => setTypeDocument({ ...typeDocument, description: e.target.value })}
              error={Boolean(errors?.description)}
              helperText={errors?.description}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Ordem de exibição"
              placeholder="Informe a order a ser utilizada na coleta do documento"
              value={typeDocument?.order}
              onChange={e => {
                if (Number(e.target.value) >= 0) {
                  setTypeDocument({ ...typeDocument, order: parseInt(`${e.target.value}`) | 0 });
                }
              }}
              error={Boolean(errors?.order)}
              helperText={errors?.order}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ArrowDropUpIcon sx={{ marginBottom: '-5px', cursor: 'pointer' }} onClick={incrementDay} />
                      <ArrowDropDownIcon sx={{ marginTop: '-5px', cursor: 'pointer' }} onClick={decrementDay} />
                    </div>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Quantidade de documentos permitidos por ano"
              placeholder="Informe o limte a ser parametrizado por tipo"
              value={typeDocument?.documentsAllowYear}
              InputLabelProps={{ shrink: true }}
              onChange={e => {
                if (Number(e.target.value) >= 0) {
                  setTypeDocument({ ...typeDocument, documentsAllowYear: Number(e.target.value) });
                }
              }}
              error={Boolean(errors?.documentsAllowYear)}
              helperText={errors?.documentsAllowYear}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ArrowDropUpIcon
                        sx={{ marginBottom: '-5px', cursor: 'pointer' }}
                        onClick={incrementAllowDocument}
                      />
                      <ArrowDropDownIcon
                        sx={{ marginTop: '-5px', cursor: 'pointer' }}
                        onClick={decrementAllowDocument}
                      />
                    </div>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Qntde. Dias Padrão"
              placeholder="Informe a Quantidade dias padrão do tipo de documento"
              value={typeDocument?.quantityTimeInDaysDefault}
              InputLabelProps={{ shrink: true }}
              onChange={e => {
                if (Number(e.target.value) >= 0) {
                  setTypeDocument({ ...typeDocument, quantityTimeInDaysDefault: Number(e.target.value) });
                }
              }}
              error={Boolean(errors?.quantityTimeInDaysDefault)}
              helperText={errors?.quantityTimeInDaysDefault}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ArrowDropUpIcon
                        sx={{ marginBottom: '-5px', cursor: 'pointer' }}
                        onClick={incrementQuantityTimeInDaysDefault}
                      />
                      <ArrowDropDownIcon
                        sx={{ marginTop: '-5px', cursor: 'pointer' }}
                        onClick={decrementQuantityTimeInDaysDefault}
                      />
                    </div>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
            >
              <TimePicker
                label="Quantidade de horas padrão"
                value={startTime}
                onChange={newValue => {
                  const decimal = newValue ? Number((newValue.hour() + newValue.minute() / 60).toFixed(2)) : null;
                  const decimalString = decimal !== null ? decimal.toString() : '';
                  setTypeDocument({ ...typeDocument, quantityTimeInHoursDefault: Number(decimalString) });
                }}
                renderInput={params => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    {...params}
                    size="medium"
                    error={Boolean(errors?.startTime)}
                    helperText={errors?.startTime}
                    placeholder="Selecione o início"
                    onBlur={() => null}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="text"
              fullWidth
              label="Texto complementar"
              placeholder="Informe o texto complementar"
              value={typeDocument.additionalText ?? ''}
              InputLabelProps={{ shrink: true }}
              onChange={e => setTypeDocument({ ...typeDocument, additionalText: e.target.value })}
              error={Boolean(errors?.additionalText)}
              helperText={errors?.additionalText}
            />

          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Rotina HCM"
              value={typeDocument.hcmRoutine}
              onChange={e => setTypeDocument({ ...typeDocument, hcmRoutine: e.target.value as keyof IHcmRoutine })}
            >
              {Object.entries(hcmRoutineLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid container item spacing={1}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 3, m: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                Parâmetros gerais do documento
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Configurações gerais para esse tipo de documento
              </Typography>
              <Divider />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2.5 }}>
                <SelectableCard
                  label="Ativo"
                  description="Tipo habilitado no sistema"
                  icon={CheckCircleOutlineIcon}
                  color="success"
                  selected={typeDocument.isActive}
                  onClick={() => setTypeDocument({ ...typeDocument, isActive: !typeDocument.isActive })}
                />
                <SelectableCard
                  label="Disponível para o colaborador"
                  description="Visível no portal do colaborador"
                  icon={PeopleAltOutlinedIcon}
                  color="success"
                  selected={typeDocument.availableToEmployee}
                  onClick={() => setTypeDocument({ ...typeDocument, availableToEmployee: !typeDocument.availableToEmployee })}
                />
              </Box>

              <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 3, p: 2, borderRadius: 1,
                bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                border: 1,
                borderColor: 'divider',
              }}
              >
                <Typography variant="body2" color="text.secondary">
                  Atestados enviados após
                </Typography>

                <TextField
                  value={typeDocument?.hoursOfLateness}
                  onChange={e => setTypeDocument({ ...typeDocument, hoursOfLateness: parseInt(e.target.value) })}
                  type="number"
                  size="small"
                  variant="outlined"
                  label="Horas"
                  sx={{
                    width: 100,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel shrink>Validação</InputLabel>
                  <Select
                    value={typeDocument?.validationOptionId}
                    onChange={e => setTypeDocument({ ...typeDocument, validationOptionId: e.target.value as number })}
                    variant="outlined"
                    label="Validação"
                    notched
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    {validationOptions.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 3, m: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                Permissão de lançamento do documento
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Configure as permissões do colaborador para esse tipo de documento
              </Typography>
              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 2.5 }}>
                <SelectableCard
                  label="Permitir observação"
                  description="Campo de texto livre"
                  icon={ChatOutlinedIcon}
                  color="success"
                  selected={typeDocument.allowComments}
                  onClick={() => setTypeDocument({ ...typeDocument, allowComments: !typeDocument.allowComments })}
                />
                <SelectableCard
                  label="Lançar em dias"
                  description="Quantidade de dias"
                  icon={CalendarTodayOutlinedIcon}
                  color="success"
                  selected={typeDocument.launchInDay}
                  onClick={() => {
                    if (typeDocument.launchInDay && !typeDocument.launchInHours) {
                      return;
                    }
                    setTypeDocument({ ...typeDocument, launchInDay: !typeDocument.launchInDay });
                  }}
                />
                <SelectableCard
                  label="Lançar em horas"
                  description="Quantidade de horas"
                  icon={AccessTimeOutlinedIcon}
                  color="success"
                  selected={typeDocument.launchInHours}
                  onClick={() => {
                    if (typeDocument.launchInHours && !typeDocument.launchInDay) {
                      return;
                    }
                    setTypeDocument({ ...typeDocument, launchInHours: !typeDocument.launchInHours });
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, m: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Campos do lançamento do documento
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Configure quais campos serão exibidos e sua obrigatoriedade.
              </Typography>
              <Divider />
              {FIELD_CONFIG.map((field, index) => {
                const isVisible = Boolean(typeDocument[field.showKey]);
                const isRequired = Boolean(typeDocument[field.reqKey]);
                const badge = getStatusBadge(isVisible, isRequired);

                return (
                  <Box key={field.showKey}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                      <FormControlLabel
                        label={
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color={isVisible ? 'text.primary' : 'text.secondary'}
                          >
                            {field.label}
                          </Typography>
                        }
                        control={
                          <Checkbox
                            checked={isVisible}
                            onChange={e => {
                              const updates = { [field.showKey]: e.target.checked };
                              if (!e.target.checked) updates[field.reqKey] = false;
                              setTypeDocument(prev => ({ ...prev, ...updates }));
                            }}
                            color="success"
                            size="small"
                          />
                        }
                      />
                      <Chip label={badge.label} color={badge.color} size="small" variant="outlined" />
                    </Box>

                    <Collapse in={isVisible}>
                      <Box sx={{ ml: 2.75, mt: 0.5, mb: 0.5 }}>
                        <FormControlLabel
                          label={
                            <Typography variant="body2" color="text.secondary">
                              Preenchimento obrigatório - {field.label}
                            </Typography>
                          }
                          control={
                            <Checkbox
                              checked={isRequired}
                              onChange={e =>
                                setTypeDocument(prev => ({ ...prev, [field.reqKey]: e.target.checked }))
                              }
                              size="small"
                            />
                          }
                        />
                      </Box>
                    </Collapse>

                    {index < FIELD_CONFIG.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Form>
      <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2} mt={0.5}>
        <Grid item>
          <LoadingButton size="large" loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
            Cancelar
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton size="large" loading={loading} variant="contained" color="success" onClick={handleSaveClick} disabled={!isEdit}>
            <SaveIcon sx={{ mr: 1 }} />
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DocumentTypeForm;
